// src/utils/clinicalUtils.js
// Shared clinical reference data used by SOAPNoteAI and ResearchModule

export const MRC_OPTIONS = [
  { value: "",   label: "Select grade..." },
  { value: "0",  label: "0 — No contraction" },
  { value: "1",  label: "1 — Flicker / trace contraction" },
  { value: "2",  label: "2 — Movement, gravity eliminated" },
  { value: "3",  label: "3 — Movement against gravity" },
  { value: "4-", label: "4− — Against slight resistance" },
  { value: "4",  label: "4 — Against moderate resistance" },
  { value: "4+", label: "4+ — Against strong resistance" },
  { value: "5",  label: "5 — Normal strength" },
];

export const JOINT_CONFIGS = {
  knee: {
    label: "Knee",
    rom: [
      { id: "knee_flex", label: "Knee Flexion",    normal: "135", unit: "°" },
      { id: "knee_ext",  label: "Knee Extension",  normal: "0",   unit: "°" },
    ],
    mmt: [
      { id: "mmt_quad",     label: "Quadriceps (Knee Extensors)" },
      { id: "mmt_hamstring",label: "Hamstrings (Knee Flexors)" },
      { id: "mmt_glut_med", label: "Gluteus Medius" },
      { id: "mmt_glut_max", label: "Gluteus Maximus" },
    ],
  },
  shoulder: {
    label: "Shoulder",
    rom: [
      { id: "sh_flex", label: "Shoulder Flexion",    normal: "180", unit: "°" },
      { id: "sh_ext",  label: "Shoulder Extension",  normal: "60",  unit: "°" },
      { id: "sh_abd",  label: "Shoulder Abduction",  normal: "180", unit: "°" },
      { id: "sh_er",   label: "External Rotation",   normal: "90",  unit: "°" },
      { id: "sh_ir",   label: "Internal Rotation",   normal: "70",  unit: "°" },
    ],
    mmt: [
      { id: "mmt_deltoid",  label: "Deltoid" },
      { id: "mmt_supra",    label: "Supraspinatus" },
      { id: "mmt_infra",    label: "Infraspinatus / Teres Minor" },
      { id: "mmt_subscap",  label: "Subscapularis" },
      { id: "mmt_serratus", label: "Serratus Anterior" },
    ],
  },
  lumbar: {
    label: "Lumbar Spine",
    rom: [
      { id: "lx_flex",  label: "Lumbar Flexion",        normal: "60", unit: "°" },
      { id: "lx_ext",   label: "Lumbar Extension",       normal: "25", unit: "°" },
      { id: "lx_lat_r", label: "Lateral Flexion Right",  normal: "25", unit: "°" },
      { id: "lx_lat_l", label: "Lateral Flexion Left",   normal: "25", unit: "°" },
      { id: "slr",      label: "SLR Angle",              normal: "80", unit: "°" },
    ],
    mmt: [
      { id: "mmt_core",      label: "Core / Transversus Abdominis" },
      { id: "mmt_multifidus",label: "Multifidus" },
      { id: "mmt_hip_flex",  label: "Hip Flexors (Iliopsoas)" },
      { id: "mmt_glut_max",  label: "Gluteus Maximus" },
    ],
  },
  cervical: {
    label: "Cervical Spine",
    rom: [
      { id: "cx_flex",  label: "Cervical Flexion",      normal: "50", unit: "°" },
      { id: "cx_ext",   label: "Cervical Extension",    normal: "60", unit: "°" },
      { id: "cx_rot_r", label: "Rotation Right",        normal: "80", unit: "°" },
      { id: "cx_rot_l", label: "Rotation Left",         normal: "80", unit: "°" },
      { id: "cx_lat_r", label: "Lateral Flexion Right", normal: "45", unit: "°" },
      { id: "cx_lat_l", label: "Lateral Flexion Left",  normal: "45", unit: "°" },
    ],
    mmt: [
      { id: "mmt_dnf",        label: "Deep Neck Flexors" },
      { id: "mmt_upper_trap", label: "Upper Trapezius" },
      { id: "mmt_scm",        label: "Sternocleidomastoid" },
      { id: "mmt_lower_trap", label: "Lower Trapezius" },
    ],
  },
  ankle: {
    label: "Ankle & Foot",
    rom: [
      { id: "ank_df", label: "Dorsiflexion",    normal: "20", unit: "°" },
      { id: "ank_pf", label: "Plantarflexion",  normal: "50", unit: "°" },
      { id: "ank_inv",label: "Inversion",       normal: "35", unit: "°" },
      { id: "ank_ev", label: "Eversion",        normal: "25", unit: "°" },
    ],
    mmt: [
      { id: "mmt_gastroc",   label: "Gastrocnemius / Soleus" },
      { id: "mmt_tib_ant",   label: "Tibialis Anterior" },
      { id: "mmt_peroneals", label: "Peroneals" },
    ],
  },
  hip: {
    label: "Hip",
    rom: [
      { id: "hip_flex", label: "Hip Flexion",           normal: "120", unit: "°" },
      { id: "hip_ext",  label: "Hip Extension",         normal: "30",  unit: "°" },
      { id: "hip_abd",  label: "Hip Abduction",         normal: "45",  unit: "°" },
      { id: "hip_er",   label: "Hip External Rotation", normal: "45",  unit: "°" },
      { id: "hip_ir",   label: "Hip Internal Rotation", normal: "45",  unit: "°" },
    ],
    mmt: [
      { id: "mmt_glut_max",  label: "Gluteus Maximus" },
      { id: "mmt_glut_med",  label: "Gluteus Medius" },
      { id: "mmt_hip_flex",  label: "Iliopsoas (Hip Flexors)" },
      { id: "mmt_adductors", label: "Hip Adductors" },
    ],
  },
  elbow: {
    label: "Elbow / Wrist / Hand",
    rom: [
      { id: "elb_flex",   label: "Elbow Flexion",    normal: "145", unit: "°" },
      { id: "elb_ext",    label: "Elbow Extension",  normal: "0",   unit: "°" },
      { id: "fore_sup",   label: "Supination",       normal: "90",  unit: "°" },
      { id: "fore_pro",   label: "Pronation",        normal: "90",  unit: "°" },
      { id: "wrist_ext",  label: "Wrist Extension",  normal: "70",  unit: "°" },
      { id: "wrist_flex", label: "Wrist Flexion",    normal: "80",  unit: "°" },
    ],
    mmt: [
      { id: "mmt_biceps",    label: "Biceps Brachii" },
      { id: "mmt_triceps",   label: "Triceps" },
      { id: "mmt_wrist_ext", label: "Wrist Extensors" },
      { id: "mmt_wrist_flex",label: "Wrist Flexors" },
      { id: "mmt_grip",      label: "Grip Strength" },
    ],
  },
};

export function detectJoint(condition) {
  const c = (condition || "").toLowerCase();
  if (c.includes("knee") || c.includes("acl") || c.includes("pcl") ||
      c.includes("meniscus") || c.includes("osteoarthritis") || c.includes("patell"))
    return "knee";
  if (c.includes("shoulder") || c.includes("rotator") || c.includes("slap") ||
      c.includes("impingement") || c.includes("frozen") || c.includes("periarthritis"))
    return "shoulder";
  if (c.includes("lumbar") || c.includes("disc") || c.includes("back") ||
      c.includes("spondylosis") || c.includes("sciatica"))
    return "lumbar";
  if (c.includes("cervical") || c.includes("neck") || c.includes("whiplash"))
    return "cervical";
  if (c.includes("ankle") || c.includes("plantar") || c.includes("achilles") ||
      c.includes("foot") || c.includes("heel"))
    return "ankle";
  if (c.includes("hip") || c.includes("fai") || c.includes("trochant"))
    return "hip";
  if (c.includes("elbow") || c.includes("epicondyl") || c.includes("tennis") ||
      c.includes("wrist") || c.includes("carpal"))
    return "elbow";
  return "lumbar"; // safe default
}